import {
  CONFIGPARAM_VISIBILITYCONDITION_TYPES,
  TpChartManifest,
  TpChartManifestAspectDef,
  TpChartManifestCompoundAspectDef,
  TpChartManifestConfigParamDef,
} from '../charts/interfaces';

import styles from './ChartTypesDocumentation.module.scss';
import Markdown from 'react-markdown';
import { CONFIGPARAMTYPES } from '../helpers/configParams';
import { discreteColorSets, numericalColorSets } from '../helpers/colors';

function renderIdentifier(content: any): any {
  return <code>{content}</code>;
}

function limitRenderWidth(content: any, maxWidth: number): any {
  return <div style={{ maxWidth: `${maxWidth}px` }}>{content}</div>;
}

function renderAspectDataType(dataType: any) {
  let content = dataType;
  if (Array.isArray(dataType)) content = dataType.map((dt) => String(dt)).join(' or ');
  return content;
}

function renderAspectNotes(aspect: TpChartManifestAspectDef) {
  const notes: string[] = [];
  if (aspect.description) notes.push(aspect.description);
  if (aspect.onColumnItems) notes.push('Should be bound to an additional table that defines the columns.');
  return limitRenderWidth(<Markdown>{notes.join('\n\n')}</Markdown>, 400);
}

function renderCompoundAspectNotes(aspect: TpChartManifestCompoundAspectDef) {
  const notes: string[] = [];
  if (aspect.description) notes.push(aspect.description);
  return limitRenderWidth(<Markdown>{notes.join('\n\n')}</Markdown>, 400);
}

function renderparamNotes(chartType: TpChartManifest, param: TpChartManifestConfigParamDef) {
  const notes: string[] = [];
  if (param.description) notes.push(param.description);
  if (param.visibilityConditions) {
    const conditions = param.visibilityConditions.map((condit) => {
      let conditString = '';
      if (condit.conditionType == CONFIGPARAM_VISIBILITYCONDITION_TYPES.FACET_BOUND) {
        conditString = `the \`${condit.conditionInfo.facet}\` aspect is defined`;
        if (condit.conditionInfo.dataType) conditString += ` as ${renderAspectDataType(condit.conditionInfo.dataType)}`;
      }
      if (condit.conditionType == CONFIGPARAM_VISIBILITYCONDITION_TYPES.CONFIG_VALUE) {
        conditString = `configuration parameter \`${condit.conditionInfo.configParam}\` equals \`${condit.conditionInfo.configValue}\``;
      }
      if (condit.conditionType == CONFIGPARAM_VISIBILITYCONDITION_TYPES.CONFIG_ABSENT) {
        conditString = `configuration parameter \`${condit.conditionInfo.configParam}\` not defined`;
      }
      if (condit.conditionType == CONFIGPARAM_VISIBILITYCONDITION_TYPES.CONFIG_PRESENT) {
        conditString = `configuration parameter \`${condit.conditionInfo.configParam}\` defined`;
      }
      if (condit.conditionType == CONFIGPARAM_VISIBILITYCONDITION_TYPES.ANY_CONFIG_PRESENT) {
        conditString = `any of \`${condit.conditionInfo.configParams.join(', ')}\` configuration parameters defined`;
      }
      if (condit.conditionType == CONFIGPARAM_VISIBILITYCONDITION_TYPES.FACET_UNBOUND) {
        conditString = `the \`${condit.conditionInfo.facet}\` aspect is not defined`;
      }
      return conditString;
    });
    notes.push(`Only applicable if ${conditions.join(' and ')}`);
  }
  return limitRenderWidth(<Markdown>{notes.join('\n\n')}</Markdown>, 450);
}

function renderParamDataType(dataType: any) {
  let resp: any = '---';
  if (dataType.configParamTypeId == CONFIGPARAMTYPES.Range) {
    resp = (
      <div className={styles.cellSection}>
        Range: {dataType.minVal} - {dataType.maxVal}
        <br />
        Default: {dataType.defaultValue}
      </div>
    );
  }
  if (dataType.configParamTypeId == CONFIGPARAMTYPES.MultipleChoice) {
    resp = (
      <div>
        Multiple choice:
        {dataType.choices.map((choice: any) => (
          <div key={choice.id}>
            <code>{choice.id}</code>: {choice.name}
          </div>
        ))}
      </div>
    );
  }
  if (dataType.configParamTypeId == CONFIGPARAMTYPES.Boolean) {
    resp = (
      <div className={styles.cellSection}>
        Boolean
        <br />
        Default: {String(dataType.defaultValue)}
      </div>
    );
  }
  if (dataType.configParamTypeId == CONFIGPARAMTYPES.DiscreteColorScheme) {
    resp = <div className={styles.cellSection}>{discreteColorSets.map((set) => `"${set.name}"`).join(', ')}</div>;
  }
  if (dataType.configParamTypeId == CONFIGPARAMTYPES.NumericalColorScheme) {
    resp = <div className={styles.cellSection}>{numericalColorSets.map((set) => `"${set.name}"`).join(', ')}</div>;
  }
  if (dataType.configParamTypeId == CONFIGPARAMTYPES.Value) {
    resp = <div className={styles.cellSection}>Value</div>;
  }
  if (dataType.configParamTypeId == CONFIGPARAMTYPES.JSON) {
    resp = <div className={styles.cellSection}>JSON structure</div>;
  }
  if (dataType.configParamTypeId == CONFIGPARAMTYPES.String) {
    resp = <div className={styles.cellSection}>String</div>;
  }
  return limitRenderWidth(resp, 350);
}

function renderAspects(aspects: TpChartManifestAspectDef[]) {
  return (
    <table>
      <thead>
        <tr>
          <td>Identifier</td>
          <td>Name</td>
          <td>Data type</td>
          <td>Required</td>
          <td>Notes</td>
        </tr>
      </thead>
      <tbody>
        {aspects.map((aspect) => (
          <tr key={aspect.id}>
            <td>{renderIdentifier(aspect.id)}</td>
            <td>
              <div className={styles.cellSection}>
                <b>{limitRenderWidth(aspect.name, 200)}</b>
              </div>
            </td>
            <td>{limitRenderWidth(renderAspectDataType(aspect.dataType), 170)}</td>
            <td>{aspect.required ? 'Yes' : 'No'}</td>
            <td>{renderAspectNotes(aspect)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function renderCompoundAspects(aspects: TpChartManifestCompoundAspectDef[]) {
  return (
    <table>
      <thead>
        <tr>
          <td>Identifier</td>
          <td>Name</td>
          <td>Data type</td>
          <td>Required</td>
          <td>Notes</td>
        </tr>
      </thead>
      <tbody>
        {aspects.map((aspect) => (
          <tr key={aspect.id}>
            <td>{renderIdentifier(aspect.id)}</td>
            <td>
              <div className={styles.cellSection}>
                <b>{limitRenderWidth(aspect.name, 200)}</b>
              </div>
            </td>
            <td>{renderAspectDataType(aspect.dataType)}</td>
            <td>{aspect.required ? 'Yes' : 'No'}</td>
            <td>{renderCompoundAspectNotes(aspect)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ChartTypeDetailed({ chartType }: { chartType: TpChartManifest }) {
  return (
    <div className={styles.detailedContainer}>
      <h1>{chartType.name}</h1>
      <div>
        Identifier: <code>{chartType.id}</code>
      </div>

      {chartType.description && <Markdown>{chartType.description}</Markdown>}

      <div>
        {chartType.examples &&
          chartType.examples.map((example) => (
            <div key={example.id} style={{ display: 'inline-block' }}>
              <img src={`/chartsamples/${example.id}.png`} className={styles.detailedExampleThumbnail} />
            </div>
          ))}
      </div>

      <h2>Aspects</h2>
      {renderAspects(chartType.aspects)}

      {chartType.compoundAspects && (
        <>
          <h2>Compound Aspects</h2>
          {renderCompoundAspects(chartType.compoundAspects)}
        </>
      )}

      {chartType.secondTable && (
        <>
          <h2>Secondary Table Aspects</h2>
          {renderAspects(chartType.secondTable!.aspects)}
        </>
      )}

      <h2>Configuration Parameters</h2>
      <table>
        <thead>
          <tr>
            <td>Identifier</td>
            <td>Name</td>
            <td>Data type</td>
            <td>Notes</td>
          </tr>
        </thead>
        <tbody>
          {chartType.configParams.map((param) => (
            <tr key={param.id}>
              <td>{renderIdentifier(param.id)}</td>
              <td>
                <div className={styles.cellSection}>
                  <b>{limitRenderWidth(param.name, 200)}</b>
                </div>
              </td>
              <td>{renderParamDataType(param.paramType)}</td>
              <td>{renderparamNotes(chartType, param)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default ChartTypeDetailed;
